// Shared types for all HR API domain modules

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  detail_description?: string;
  icon?: string;
  media_url?: string | null;
  target_menu?: string | null;
  cta_text?: string | null;
  cta_action?: string | null;
}

export interface CareerItem {
  company?: string;
  position?: string;
  department?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
}

export interface EducationItem {
  school?: string;
  degree?: string;
  major?: string;
  gpa?: string;
  admission_date?: string;
  graduation_date?: string;
}

export interface CertificationItem {
  name?: string;
  certification?: string;
  date?: string;
  issuer?: string;
}

export interface EmployeeTitle {
  id?: number;
  title_id?: number;
  name: string;
  level: number;
}

export interface Employee {
  id: string;
  employee_id?: string;
  /** When false, UI skips GET /api/employees/:id/photo (no uploaded file). Omitted on older API responses. */
  has_profile_photo?: boolean;
  employee_no?: string;
  legacy_id?: string;
  name: string;
  age: number;
  position: string;
  team: string;
  join_date: string;
  birth_date: string;
  contact: string;
  email: string;
  work_email?: string;
  phone?: string;
  gender?: string;
  address?: string;
  salary?: number;
  skills?: string[];
  rank_id?: number;
  rank_name?: string;
  rank_code?: string;
  title_id?: number;
  title_name?: string;
  title_code?: string;
  rank_level?: number;
  title_level?: number;
  titles?: EmployeeTitle[];
  job_category_id?: number;
  job_category_code?: string;
  job_category_name?: string;
  org_unit_id?: number;
  org_unit_code?: string;
  org_unit_name?: string;
  company_id?: number;
  company_name?: string;
  company_code?: string;
  education: EducationItem[];
  career: CareerItem[];
  certifications: CertificationItem[];
  additional_notes?: string;
  swot: {
    strengths: string[];
    weaknesses: string[];
    opportunities: string[];
    threats: string[];
  };
  analysis?: {
    career_analysis: string;
    strengths: string[];
    weaknesses: string[];
    utilization: string[];
    risk_factors: string[];
    development_priority: string[];
    market_value: {
      salary_assessment: string;
      competitiveness: string;
    };
  };
}

export interface EmployeeUpdateData {
  name?: string;
  position?: string;
  team?: string;
  join_date?: string;
  birth_date?: string;
  age?: number;
  gender?: string;
  email?: string;
  work_email?: string;
  phone?: string;
  contact?: string;
  address?: string;
  salary?: number;
  skills?: string[];
  career?: CareerItem[];
  education?: EducationItem[];
  certifications?: CertificationItem[];
  additional_notes?: string;
  auto_reanalyze?: boolean;
  job_category_id?: number;
}

export interface ManualEmployeeCreateData {
  name: string;
  team?: string;
  position?: string;
  title?: string;
  org_unit_id?: number;
  join_date?: string;
  birth_date?: string;
  gender?: string;
  phone?: string;
  email?: string;
  work_email?: string;
  address?: string;
  salary?: number;
  career?: CareerItem[];
  education?: EducationItem[];
  certifications?: CertificationItem[];
  skills?: string[];
  additional_notes?: string;
  data_source?: string;
  original_filename?: string;
}

export interface ExtractedEmployeeResponse {
  message: string;
  extracted_data: Record<string, any>;
  filename: string;
  file_type: string;
}

export interface ManualEmployeeResponse {
  message: string;
  employee_id: string;
  employee: Employee;
}

export interface TeamAnalysis {
  team: string;
  total_employees: number;
  admin_comment?: string;
  analysis: {
    team_composition: string;
    capability_gaps: Record<string, string>;
    efficiency_issues: string[];
    team_strengths: string[];
    critical_improvements: string[];
    hiring_needs: string[];
  };
}

export interface TeamCommentUpdateData {
  admin_comment: string;
  auto_reanalyze?: boolean;
}

export interface Insights {
  summary: string;
  structural_issues: string[];
  skill_gaps: string[];
  risk_points: string[];
  immediate_actions: string[];
  hiring_strategy: string[];
  cost_efficiency: string;
}

export interface Statistics {
  total_employees: number;
  total_teams: number;
  average_age: number;
  team_distribution: Record<string, number>;
  position_distribution: Record<string, number>;
  age_distribution: {
    min: number;
    max: number;
    ages: number[];
  };
}

export interface RegionDistribution {
  region_distribution: Record<string, number>;
  sub_regions?: Record<string, Record<string, number>>;
  total_with_address: number;
  total_without_address: number;
  total_employees: number;
}

export type NoteType = 'general' | 'annual_performance' | 'mid_year_check' | 'onboarding' | 'resignation' | 'grievance' | 'career' | 'disciplinary';

export const NOTE_TYPE_LABELS: Record<NoteType, string> = {
  general: '일반 면담',
  annual_performance: '정기 인사평가',
  mid_year_check: '중간 면담',
  onboarding: '입사 면담',
  resignation: '퇴사 면담',
  grievance: '고충 상담',
  career: '경력 개발 면담',
  disciplinary: '징계 면담',
};

export interface Evaluation {
  id: number;
  evaluation_text: string;
  evaluator_name?: string;
  created_at: number;
  formatted_date: string;
  interview_date?: string;
  note_type?: NoteType;
}

export interface EvaluationCreateData {
  evaluation_text: string;
  evaluator_name?: string;
  auto_reanalyze?: boolean;
  interview_date?: string;
  note_type?: NoteType;
}

export interface EmployeeEvaluationResponse {
  employee_id: string;
  evaluations: Evaluation[];
  total: number;
}

export interface TeamEvaluationResponse {
  team_name: string;
  evaluations: Evaluation[];
  total: number;
}

export interface PdfUploadResponse {
  message: string;
  employee_id: string;
  team: string;
  employee: Employee;
  overwritten: boolean;
}

export interface PersonnelChange {
  id: number;
  employee_id: string;
  employee_name?: string;
  change_type: 'join' | 'resign' | 'transfer_in' | 'transfer_out' | 'team_change' | 'position_change' | 'leave' | 'return';
  change_date: string;
  previous_value?: string;
  new_value?: string;
  description?: string;
  created_at: number;
  formatted_date: string;
}

export interface PersonnelChangeCreateData {
  change_type: string;
  change_date: string;
  previous_value?: string;
  new_value?: string;
  description?: string;
}

export interface PersonnelChangesResponse {
  changes: PersonnelChange[];
  total: number;
}

export interface CohortMember {
  employee_id: string;
  name: string;
  status: string;
}

export interface Cohort {
  cohort_id: string;
  join_count: number;
  current_count: number;
  retention_rate: number;
  promoted_count: number;
  promotion_rate: number;
  resigned_count: number;
  attrition_rate: number;
  avg_tenure_months: number;
  members: CohortMember[];
}

export interface CohortAnalysisData {
  group_by: string;
  cohorts: Cohort[];
  summary: {
    total_cohorts: number;
    overall_retention: number;
    best_cohort: string;
    worst_cohort: string;
  };
}

export interface DEIDistribution {
  category: string;
  count: number;
  ratio: number;
}

export interface DEITeamGender {
  team: string;
  male: number;
  female: number;
  total: number;
  female_ratio: number;
}

export interface DEIAnalysisData {
  gender_distribution: DEIDistribution[];
  age_distribution: DEIDistribution[];
  region_distribution: DEIDistribution[];
  team_gender: DEITeamGender[];
  position_gender: DEITeamGender[];
  summary: {
    total_employees: number;
    gender_balance_score: number;
    age_diversity_index: number;
    region_count: number;
  };
}

export interface EmployeeStatus {
  employee_id: string;
  status: 'active' | 'resigned' | 'on_leave' | 'transferred';
  status_date?: string;
  address?: string;
  updated_at?: number;
}

export interface EmployeeStatusUpdateData {
  status: string;
  status_date?: string;
  address?: string;
}

export interface PaginationResponse {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface EmployeesResponse {
  employees: Employee[];
  pagination: PaginationResponse;
}

// Evaluation, goal, and salary-band types moved to evaluationTypes.ts (MYB-44)
export * from './evaluationTypes';

